import type {
  OperationStoreState,
  StoreAction,
  LifecycleMarker,
  NormalizedFastForwardResponse,
} from './types'
import { MARKER_CAP } from './types'
import type {
  Operation,
  Order,
  OperationOpen,
  OperationClosed,
  TournamentEvent,
} from '../sdk'

export function createInitialState(): OperationStoreState {
  return {
    operationsById: new Map(),
    pendingIds: new Set(),
    openIds: new Set(),
    closedIds: new Set(),
    latestPricesBySymbol: {},
    lifecycleMarkers: [],
    markerIdSet: new Set(),
    hydrated: false,
    currentReplayTime: undefined,
    currentReplayTimeDisplay: undefined,
    remainingMatchTime: undefined,
  }
}

export function reduce(
  state: OperationStoreState,
  action: StoreAction
): OperationStoreState {
  switch (action.type) {
    case 'HYDRATE':
      return hydrateState(state, action.operations)
    case 'TOURNAMENT_EVENT':
      return applyTournamentEvent(state, action.event)
    case 'PRICE_UPDATED':
      return applyPriceUpdate(state, action.symbol, action.price)
    case 'REQUEST_ACK_POSITION_OPENED':
      return upsertOperation(state, { state: 'open', operationOpen: action.data })
    case 'REQUEST_ACK_ORDER_PLACED':
      return upsertOperation(state, { state: 'order', order: action.data })
    case 'REQUEST_ACK_ORDER_CANCELLED':
      return upsertOperation(state, { state: 'order', order: action.data })
    case 'REQUEST_ACK_POSITION_CLOSED':
      return upsertOperation(state, { state: 'closed', operationClosed: action.data })
    case 'REQUEST_ACK_OPERATION_UPDATED':
      return upsertOperation(state, action.data)
    case 'FAST_FORWARD_RESPONSE':
      return applyFastForwardResponse(state, action.data)
    case 'RESET':
      return createInitialState()
    default:
      return state
  }
}

// ========== Helper Functions ==========

function extractOperationId(op: Operation): string {
  switch (op.state) {
    case 'order':
      return op.order.id
    case 'open':
      return op.operationOpen.order.id
    case 'closed':
      return op.operationClosed.order.id
  }
}

function getOrder(op: Operation): Order {
  switch (op.state) {
    case 'order':
      return op.order
    case 'open':
      return op.operationOpen.order
    case 'closed':
      return op.operationClosed.order
  }
}

function operationStateRank(op: Operation): number {
  switch (op.state) {
    case 'order':
      return 0
    case 'open':
      return 1
    case 'closed':
      return 2
  }
}

function isOperationPayloadEqual(op1: Operation, op2: Operation): boolean {
  // Compare payloads for same-state operations
  // Returns true if operations are identical (no update needed), false if different
  if (op1.state !== op2.state) {
    return false
  }

  switch (op1.state) {
    case 'order': {
      if (op2.state !== 'order') return false
      const order1 = op1.order
      const order2 = op2.order
      return (
        order1.id === order2.id &&
        order1.side === order2.side &&
        order1.symbol === order2.symbol &&
        order1.lots === order2.lots &&
        order1.type === order2.type &&
        order1.status === order2.status &&
        order1.targetPrice === order2.targetPrice &&
        order1.stopLossPrice === order2.stopLossPrice &&
        order1.takeProfitPrice === order2.takeProfitPrice
      )
    }

    case 'open': {
      if (op2.state !== 'open') return false
      const open1 = op1.operationOpen
      const open2 = op2.operationOpen
      return (
        open1.order.id === open2.order.id &&
        open1.order.side === open2.order.side &&
        open1.order.symbol === open2.order.symbol &&
        open1.order.lots === open2.order.lots &&
        open1.order.stopLossPrice === open2.order.stopLossPrice &&
        open1.order.takeProfitPrice === open2.order.takeProfitPrice &&
        open1.openPrice === open2.openPrice &&
        open1.unrealizedPnl === open2.unrealizedPnl &&
        open1.pips === open2.pips &&
        open1.computedAt === open2.computedAt
      )
    }

    case 'closed': {
      if (op2.state !== 'closed') return false
      const closed1 = op1.operationClosed
      const closed2 = op2.operationClosed
      return (
        closed1.order.id === closed2.order.id &&
        closed1.openPrice === closed2.openPrice &&
        closed1.closePrice === closed2.closePrice &&
        closed1.realizedPnl === closed2.realizedPnl
      )
    }
  }
}

function hydrateState(prevState: OperationStoreState, operations: Operation[]): OperationStoreState {
  // Build new operations map from snapshot, preserving other state
  const operationsById = new Map<string, Operation>()
  const pendingIds = new Set<string>()
  const openIds = new Set<string>()
  const closedIds = new Set<string>()

  operations.forEach((op) => {
    const id = extractOperationId(op)
    operationsById.set(id, op)
    switch (op.state) {
      case 'order':
        pendingIds.add(id)
        break
      case 'open':
        openIds.add(id)
        break
      case 'closed':
        closedIds.add(id)
        break
    }
  })

  return {
    operationsById,
    pendingIds,
    openIds,
    closedIds,
    latestPricesBySymbol: prevState.latestPricesBySymbol, // Preserve prices
    lifecycleMarkers: prevState.lifecycleMarkers, // Preserve markers
    markerIdSet: prevState.markerIdSet, // Preserve marker dedup set
    currentReplayTime: prevState.currentReplayTime, // Preserve replay time
    currentReplayTimeDisplay: prevState.currentReplayTimeDisplay, // Preserve display replay time
    remainingMatchTime: prevState.remainingMatchTime, // Preserve remaining time
    hydrated: true,
  }
}

function upsertOperation(state: OperationStoreState, operation: Operation): OperationStoreState {
  const id = extractOperationId(operation)
  const existing = state.operationsById.get(id)

  // Downgrade protection: reject if incoming rank is lower (lifecycle regression)
  if (existing && operationStateRank(operation) < operationStateRank(existing)) {
    return state
  }

  // Same-state logic: if rank equals, only update if payload differs
  if (existing && operationStateRank(operation) === operationStateRank(existing)) {
    if (isOperationPayloadEqual(existing, operation)) {
      return state // Exact duplicate, no update needed
    }
    // Payload differs, proceed with update
  }

  // Clone state collections
  const nextState: OperationStoreState = {
    ...state,
    operationsById: new Map(state.operationsById),
    pendingIds: new Set(state.pendingIds),
    openIds: new Set(state.openIds),
    closedIds: new Set(state.closedIds),
    latestPricesBySymbol: { ...state.latestPricesBySymbol },
    lifecycleMarkers: state.lifecycleMarkers,
    markerIdSet: state.markerIdSet,
  }

  // Remove from old partition
  if (existing) {
    switch (existing.state) {
      case 'order':
        nextState.pendingIds.delete(id)
        break
      case 'open':
        nextState.openIds.delete(id)
        break
      case 'closed':
        nextState.closedIds.delete(id)
        break
    }
  }

  // Add to new partition
  switch (operation.state) {
    case 'order':
      nextState.pendingIds.add(id)
      break
    case 'open':
      nextState.openIds.add(id)
      break
    case 'closed':
      nextState.closedIds.add(id)
      break
  }

  nextState.operationsById.set(id, operation)
  return nextState
}

function applyTournamentEvent(state: OperationStoreState, event: TournamentEvent): OperationStoreState {
  let nextState = state

  switch (event.type) {
    case 'ORDER_PLACED':
      nextState = upsertOperation(state, { state: 'order', order: event.data })
      nextState = appendMarker(nextState, createMarkerFromEvent(event))
      break

    case 'ORDER_EDITED':
      nextState = upsertOperation(state, { state: 'order', order: event.data })
      nextState = appendMarker(nextState, createMarkerFromEvent(event))
      break

    case 'ORDER_FAILED':
      nextState = removeOperation(state, event.data.id)
      nextState = appendMarker(nextState, createMarkerFromEvent(event))
      break

    case 'ORDER_CANCELLED':
      nextState = removeOperation(state, event.data.id)
      nextState = appendMarker(nextState, createMarkerFromEvent(event))
      break

    case 'POSITION_OPENED':
      nextState = upsertOperation(state, { state: 'open', operationOpen: event.data })
      nextState = appendMarker(nextState, createMarkerFromEvent(event))
      break

    case 'POSITION_EDITED':
      nextState = upsertOperation(state, { state: 'open', operationOpen: event.data })
      nextState = appendMarker(nextState, createMarkerFromEvent(event))
      break

    case 'POSITION_CLOSED':
      nextState = upsertOperation(state, { state: 'closed', operationClosed: event.data })
      nextState = appendMarker(nextState, createMarkerFromEvent(event))
      break

    case 'TOURNAMENT_CLOSED':
      // No-op for operations, no marker
      break
  }

  return nextState
}

function removeOperation(state: OperationStoreState, operationId: string): OperationStoreState {
  const existing = state.operationsById.get(operationId)
  if (!existing) {
    return state
  }

  const nextState: OperationStoreState = {
    ...state,
    operationsById: new Map(state.operationsById),
    pendingIds: new Set(state.pendingIds),
    openIds: new Set(state.openIds),
    closedIds: new Set(state.closedIds),
    latestPricesBySymbol: { ...state.latestPricesBySymbol },
    lifecycleMarkers: state.lifecycleMarkers,
    markerIdSet: state.markerIdSet,
  }

  switch (existing.state) {
    case 'order':
      nextState.pendingIds.delete(operationId)
      break
    case 'open':
      nextState.openIds.delete(operationId)
      break
    case 'closed':
      nextState.closedIds.delete(operationId)
      break
  }

  nextState.operationsById.delete(operationId)
  return nextState
}

function applyPriceUpdate(
  state: OperationStoreState,
  symbol: string,
  price: number
): OperationStoreState {
  return {
    ...state,
    latestPricesBySymbol: {
      ...state.latestPricesBySymbol,
      [symbol]: price,
    },
  }
}

function createMarkerFromEvent(event: TournamentEvent): LifecycleMarker {
  let price: number | undefined
  let operationId: string
  let timestamp: string

  switch (event.type) {
    case 'ORDER_PLACED':
      operationId = event.data.id
      timestamp = event.data.placedAt
      price = event.data.targetPrice ? parseFloat(event.data.targetPrice) : undefined
      break
    case 'ORDER_EDITED':
      operationId = event.data.id
      timestamp = event.data.updatedAt
      price = event.data.targetPrice ? parseFloat(event.data.targetPrice) : undefined
      break
    case 'ORDER_FAILED':
      operationId = event.data.id
      timestamp = event.data.updatedAt
      price = event.data.targetPrice ? parseFloat(event.data.targetPrice) : undefined
      break
    case 'ORDER_CANCELLED':
      operationId = event.data.id
      timestamp = event.data.updatedAt
      price = event.data.targetPrice ? parseFloat(event.data.targetPrice) : undefined
      break
    case 'POSITION_OPENED':
      operationId = event.data.order.id
      timestamp = event.data.openedAt
      price = parseFloat(event.data.openPrice)
      break
    case 'POSITION_EDITED':
      operationId = event.data.order.id
      timestamp = event.data.computedAt
      price = parseFloat(event.data.openPrice)
      break
    case 'POSITION_CLOSED':
      operationId = event.data.order.id
      timestamp = event.data.closedAt
      price = parseFloat(event.data.closePrice)
      break
    case 'TOURNAMENT_CLOSED':
      // Should not be called for TOURNAMENT_CLOSED, but provide fallback
      return {
        id: 'tournament-closed',
        operationId: 'N/A',
        eventType: event.type,
        symbol: 'N/A',
        side: 'BUY',
        price: undefined,
        timestamp: new Date().toISOString(),
      }
  }

  const order = getOrder({ state: 'order', order: event.data as Order } as any) ?? (event.data as any).order
  const symbol = order?.symbol ?? 'UNKNOWN'
  const side = (order?.side ?? 'BUY') as 'BUY' | 'SELL'

  return {
    id: `${operationId}::${event.type}::${timestamp}`,
    operationId,
    eventType: event.type,
    symbol,
    side,
    price,
    timestamp,
  }
}

function appendMarker(state: OperationStoreState, marker: LifecycleMarker): OperationStoreState {
  // Check for duplicate
  if (state.markerIdSet.has(marker.id)) {
    return state
  }

  const nextMarkers = [...state.lifecycleMarkers, marker]
  const nextMarkerIdSet = new Set(state.markerIdSet)
  nextMarkerIdSet.add(marker.id)

  // Enforce cap: remove oldest entries if exceeded
  if (nextMarkers.length > MARKER_CAP) {
    const toRemove = nextMarkers.length - MARKER_CAP
    for (let i = 0; i < toRemove; i++) {
      nextMarkerIdSet.delete(nextMarkers[i].id)
    }
    nextMarkers.splice(0, toRemove)
  }

  return {
    ...state,
    lifecycleMarkers: nextMarkers,
    markerIdSet: nextMarkerIdSet,
  }
}

function applyFastForwardResponse(state: OperationStoreState, response: NormalizedFastForwardResponse): OperationStoreState {
  // Merge new operations into state, replacing old state
  const operationsById = new Map<string, Operation>()
  const pendingIds = new Set<string>()
  const openIds = new Set<string>()
  const closedIds = new Set<string>()

  response.operations.forEach((op: Operation) => {
    const id = extractOperationId(op)
    operationsById.set(id, op)
    switch (op.state) {
      case 'order':
        pendingIds.add(id)
        break
      case 'open':
        openIds.add(id)
        break
      case 'closed':
        closedIds.add(id)
        break
    }
  })

  return {
    ...state,
    operationsById,
    pendingIds,
    openIds,
    closedIds,
    currentReplayTime: response.currentReplayTime,
    currentReplayTimeDisplay: response.currentReplayTimeDisplay,
    remainingMatchTime: response.remainingMatchTimeMs,
    latestPricesBySymbol: state.latestPricesBySymbol,
    lifecycleMarkers: state.lifecycleMarkers,
    markerIdSet: state.markerIdSet,
  }
}
